import React from 'react';
import { fmtDateTimeIST } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CompanyResumeView } from '@/components/CompanyResumeView';
import { Sparkles, Eye, RefreshCw, Download, Upload, FileX2 } from 'lucide-react';

export function CandidateResumeTabContent({
  isReadOnlyForActions,
  fileInputRef,
  onResumeFileSelected,
  onPickResumeFile,
  companyResume,
  resumeFile,
  generating,
  generateResume,
  downloadingResume,
  downloadOriginalResume,
  uploadingResume,
  canReplaceResume,
  setResumeViewOpen,
}) {
  return (
    <>
      <fieldset disabled={isReadOnlyForActions} className="contents" data-testid="resume-tab-fieldset">
        <div className="space-y-4">
          {/* Hidden file input — triggered by the Upload CTA below. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={onResumeFileSelected}
            data-testid="resume-file-input"
          />

          {/* Company Resume card */}
          <Card className="card-elevated" data-testid="company-resume-card"><CardContent className="p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                  <p className="text-sm font-semibold">Company-standard resume</p>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">Standardized into Kadel Labs template — this is what clients see in the report modal.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                {canReplaceResume && companyResume && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setResumeViewOpen(true)} data-testid="view-company-resume-button"><Eye className="h-3.5 w-3.5" /> View</Button>
                )}
                {canReplaceResume && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={generateResume}
                            disabled={generating || !resumeFile}
                            data-testid="regenerate-company-resume-button"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${generating ? 'animate-spin' : ''}`} /> {generating ? 'Parsing…' : (companyResume ? 'Regenerate' : 'Generate')}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!resumeFile && (
                        <TooltipContent side="left" data-testid="generate-disabled-tooltip">
                          Upload an original resume PDF/DOCX first.
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            {companyResume && (
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="company-resume-meta">
                <div className="rounded-lg border bg-secondary/40 p-3 text-xs"><p className="text-muted-foreground">Source</p><p className="font-medium truncate">{companyResume.source_filename || '—'}</p></div>
                <div className="rounded-lg border bg-secondary/40 p-3 text-xs"><p className="text-muted-foreground">Generated</p><p className="font-medium tabular-nums">{fmtDateTimeIST(companyResume.generated_at)}</p></div>
                {/* <div className="rounded-lg border bg-secondary/40 p-3 text-xs"><p className="text-muted-foreground">Model</p><p className="font-medium"><code className="rounded bg-card px-1">{companyResume.ai_model_version}</code></p></div> */}
              </div>
            )}
          </CardContent></Card>

          {/* Original resume card — source-of-truth file, not the standardised content */}
          <Card className="card-elevated"><CardContent className="p-6">
            {resumeFile ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="original-resume-present">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate" data-testid="original-resume-filename">{resumeFile.original_filename}</p>
                  <p className="text-xs text-muted-foreground">
                    Original {(resumeFile.content_type || '').split('/').pop()?.toUpperCase() || 'file'} · uploaded {fmtDateTimeIST(resumeFile.uploaded_at)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                  {canReplaceResume && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5"
                      disabled={uploadingResume}
                      onClick={onPickResumeFile}
                      data-testid="replace-original-resume-button"
                    >
                      <Upload className="h-4 w-4" /> {uploadingResume ? 'Uploading…' : 'Replace'}
                    </Button>
                  )}
                  {canReplaceResume && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={downloadingResume}
                      onClick={downloadOriginalResume}
                      data-testid="download-original-resume-button"
                    >
                      <Download className="h-4 w-4" /> {downloadingResume ? 'Preparing…' : 'Download'}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-3 text-left sm:flex-row sm:items-center sm:justify-between" data-testid="original-resume-empty-state">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                    <FileX2 className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">No original resume on file</p>
                    <p className="text-xs text-muted-foreground">
                      Upload the candidate&apos;s original PDF or DOCX. This unlocks the Standardised resume.
                    </p>
                  </div>
                </div>
                {canReplaceResume ? (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    disabled={uploadingResume}
                    onClick={onPickResumeFile}
                    data-testid="upload-original-resume-button"
                  >
                    <Upload className="h-4 w-4" /> {uploadingResume ? 'Uploading…' : 'Upload original resume'}
                  </Button>
                ) : null}
              </div>
            )}
          </CardContent></Card>
        </div>
      </fieldset>
    </>
  );
}

export function CompanyResumeViewModal({
  resumeViewOpen,
  setResumeViewOpen,
  candidateName,
  companyResume,
  resumeTemplate,
  photoUrl,
  generating,
  generateResume,
  canReplaceResume = true,
}) {
  return (
    <Dialog open={resumeViewOpen} onOpenChange={setResumeViewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hr-company-resume-modal">
          <DialogHeader>
            <DialogTitle>Company-standard resume — {candidateName}</DialogTitle>
            <DialogDescription>This is the standardized resume clients see in the client report.</DialogDescription>
          </DialogHeader>
          <CompanyResumeView
            resume={companyResume ? { ...companyResume.payload, generated_at: fmtDateTimeIST(companyResume.generated_at) } : null}
            template={resumeTemplate}
            photoUrl={photoUrl || null}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResumeViewOpen(false)}>Close</Button>
            {canReplaceResume && (
              <Button onClick={generateResume} disabled={generating} className="gap-1.5"><RefreshCw className={`h-3.5 w-3.5 ${generating ? 'animate-spin' : ''}`} /> {generating ? 'Parsing…' : 'Regenerate'}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
